<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  DialogFlexibleInput,
  PrimaryButton,
  SecondaryButton,
} from '@es-bill/es-bill-common-components';

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  summary: {
    type: Object,
    default: null,
  },
  submitting: {
    type: Boolean,
    default: false,
  },
  submissionProgress: {
    type: Object,
    default: () => ({ current: 0, total: 0 }),
  },
});

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel']);

const { t } = useI18n();

const submissionProgressPercent = computed(() => {
  if (props.submissionProgress.total === 0) {
    return 0;
  }
  return Math.round(
    (props.submissionProgress.current / props.submissionProgress.total) * 100
  );
});

const requestCount = computed(() => props.summary?.totals?.requestCount ?? 0);

const dialogButtons = computed(() => [
  {
    component: SecondaryButton,
    props: {
      size: 'large',
      class: 'w-100',
      text: t('cancel'),
      disabled: props.submitting,
    },
    cols: '12',
    md: '6',
    onClick: () => {
      if (!props.submitting) {
        emit('cancel');
      }
    },
  },
  {
    component: PrimaryButton,
    props: {
      size: 'large',
      class: 'w-100',
      text: t('confirm'),
      loading: props.submitting,
      disabled: props.submitting || requestCount.value === 0,
    },
    cols: '12',
    md: '6',
    onClick: () => {
      emit('confirm');
    },
  },
]);

function formatDateRanges(dateRanges) {
  return (dateRanges ?? []).join(', ');
}
</script>

<template>
  <DialogFlexibleInput
    :model-value="modelValue"
    :title="t('billrunsDetails.requestEnergyDataDialogTitle')"
    :buttons-config="dialogButtons"
    :persistent="submitting"
    max-width="960"
    class="billruns-energy-request-dialog"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <template #input-slot>
      <div class="billruns-energy-request-dialog-body">
      <div v-if="summary" class="mb-4 d-flex flex-wrap ga-4">
        <div>
          <strong>{{ t('billrunsDetails.requestSummaryKpiRequests') }}:</strong>
          {{ summary.totals.requestCount }}
        </div>
        <div>
          <strong>{{ t('billrunsDetails.requestSummaryKpiMeterPoints') }}:</strong>
          {{ summary.totals.meteringPointCount }}
        </div>
        <div>
          <strong>{{ t('billrunsDetails.requestSummaryKpiCommunities') }}:</strong>
          {{ summary.totals.communityCount }}
        </div>
        <div>
          <strong>{{ t('billrunsDetails.requestSummaryKpiDsos') }}:</strong>
          {{ summary.totals.dsoCount }}
        </div>
        <div v-if="summary.totals.unresolvedMeterPointCount > 0">
          <v-icon color="warning" size="small" class="mr-1">mdi-alert-circle</v-icon>
          <strong>{{ t('billrunsDetails.requestSummaryKpiUnresolved') }}:</strong>
          {{ summary.totals.unresolvedMeterPointCount }}
        </div>
      </div>

      <div v-if="submitting" class="mb-4">
        <p class="text-body-2 mb-2">
          {{ t('billrunsDetails.requestEnergyDataProgress', {
            current: submissionProgress.current,
            total: submissionProgress.total,
          }) }}
        </p>
        <v-progress-linear
          :model-value="submissionProgressPercent"
          color="primary"
          height="8"
          rounded
        />
      </div>

      <h3 class="text-subtitle-1 mb-2">
        {{ t('billrunsDetails.requestSummaryByCommunityTitle') }}
      </h3>
      <div class="table-scroll-wrapper mb-4 grouped-table-wrapper">
        <v-table density="compact" class="details-table">
          <thead>
            <tr>
              <th>{{ t('billrunsDetails.requestSummaryColumnName') }}</th>
              <th>{{ t('billrunsDetails.requestSummaryColumnRequests') }}</th>
              <th>{{ t('billrunsDetails.requestSummaryColumnMeterPoints') }}</th>
              <th>{{ t('billrunsDetails.requestSummaryColumnDateRanges') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in summary?.byCommunity ?? []"
              :key="row.ecid"
            >
              <td>{{ row.displayLabel }}</td>
              <td>{{ row.requestCount }}</td>
              <td>{{ row.meteringPointCount }}</td>
              <td>{{ formatDateRanges(row.dateRanges) }}</td>
            </tr>
          </tbody>
        </v-table>
      </div>

      <h3 class="text-subtitle-1 mb-2">
        {{ t('billrunsDetails.requestSummaryByDsoTitle') }}
      </h3>
      <div class="table-scroll-wrapper mb-4 grouped-table-wrapper">
        <v-table density="compact" class="details-table">
          <thead>
            <tr>
              <th>{{ t('billrunsDetails.requestSummaryColumnName') }}</th>
              <th>{{ t('billrunsDetails.requestSummaryColumnRequests') }}</th>
              <th>{{ t('billrunsDetails.requestSummaryColumnMeterPoints') }}</th>
              <th>{{ t('billrunsDetails.requestSummaryColumnDateRanges') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in summary?.byDso ?? []"
              :key="row.ecnDso"
            >
              <td>{{ row.displayLabel }}</td>
              <td>{{ row.requestCount }}</td>
              <td>{{ row.meteringPointCount }}</td>
              <td>{{ formatDateRanges(row.dateRanges) }}</td>
            </tr>
          </tbody>
        </v-table>
      </div>

      <div
        v-if="(summary?.unresolvedMeterPoints?.length ?? 0) > 0"
        class="mt-2"
      >
        <v-divider class="mb-2" />
        <div class="d-flex align-center mb-2">
          <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
          <strong>
            {{ t('billrunsDetails.requestEnergyDataUnresolvedTitle', {
              count: summary.unresolvedMeterPoints.length,
            }) }}
          </strong>
        </div>
        <p class="text-body-2 text-medium-emphasis mb-2">
          {{ t('billrunsDetails.requestSummaryUnresolvedExplanation') }}
        </p>
        <ul class="unresolved-list">
          <li
            v-for="(mp, index) in summary.unresolvedMeterPoints"
            :key="`${mp.ecid}-${mp.meteringPoint}-${index}`"
            class="text-error"
          >
            {{ mp.meteringPoint }}
            ({{ t('getEnergyData.ecn') }}: {{ mp.ecn }}, {{ t('getEnergyData.ecid') }}: {{ mp.ecid }})
          </li>
        </ul>
      </div>
      </div>
    </template>
  </DialogFlexibleInput>
</template>

<style scoped>
.table-scroll-wrapper {
  overflow-x: auto;
}

.grouped-table-wrapper {
  max-height: 300px;
  overflow: auto;
}

.unresolved-list {
  max-height: 200px;
  overflow-y: auto;
  padding-left: 1.25rem;
}

.details-table :deep(th) {
  white-space: nowrap;
}

.details-table :deep(td:first-child) {
  min-width: 220px;
}

.details-table :deep(td:last-child) {
  min-width: 200px;
  white-space: normal;
}
</style>

<style>
/* Dialog content is teleported; widen overlay when this summary is shown. */
.v-overlay:has(.billruns-energy-request-dialog-body) > .v-overlay__content {
  max-width: min(960px, 95vw) !important;
  width: min(960px, 95vw) !important;
}
</style>
